/**
 * 入力方式の抽象化(docs/03_architecture.md §3.1)。
 *
 * 言語ごとに `InputValidator` を実装することで、`TypingSession` を変えずに
 * 日本語(ローマ字)・英語(直接入力)・将来のピンイン等に対応する。
 */

import { RomajiAutomaton, type RomajiCursor } from './romaji/automaton.js';
import { toHalfWidth } from './romaji/table.js';
import type { InputMode } from './types.js';

/** 1語ぶんの入力照合器。 */
export interface WordMatcher {
  /** 1打鍵を入力する。受理できたら true(false でも状態は変化しない)。 */
  input(key: string): boolean;
  /** 語が完成したか。 */
  readonly completed: boolean;
  /** 次に受理されるキーの集合(昇順・重複なし)。完成後は空配列。 */
  readonly nextKeys: readonly string[];
  /** 受理済みの打鍵列。 */
  readonly typed: string;
  /** 残りの打鍵列(最短経路)。 */
  readonly remaining: string;
  /** ガイド表示用の全体文字列(= typed + remaining)。 */
  readonly guide: string;
  /** 読みの何文字目まで確定したか(ルビ強調用)。 */
  readonly unitIndex: number;
  /** 読みの文字数。 */
  readonly unitLength: number;
  /** 初期状態に戻す。 */
  reset(): void;
}

/** 言語ごとの入力方式。 */
export interface InputValidator {
  readonly mode: InputMode;
  /** 読み1語から照合器を作る。 */
  createMatcher(reading: string): WordMatcher;
  /** 読み1語の代表表記(ガイドの初期表示)。 */
  toDisplayString(reading: string): string;
}

/** ローマ字入力(日本語モード)。表記ゆれ・促音・撥音・拗音に対応する。 */
export class RomajiValidator implements InputValidator {
  readonly mode = 'romaji' as const;

  private readonly cache = new Map<string, RomajiAutomaton>();

  private automatonFor(reading: string): RomajiAutomaton {
    const cached = this.cache.get(reading);
    if (cached) return cached;
    const built = new RomajiAutomaton(reading);
    this.cache.set(reading, built);
    return built;
  }

  createMatcher(reading: string): WordMatcher {
    return new RomajiWordMatcher(this.automatonFor(reading).createCursor());
  }

  toDisplayString(reading: string): string {
    return this.automatonFor(reading).romaji;
  }
}

class RomajiWordMatcher implements WordMatcher {
  constructor(private readonly cursor: RomajiCursor) {}

  input(key: string): boolean {
    return this.cursor.input(key);
  }
  get completed(): boolean {
    return this.cursor.completed;
  }
  get nextKeys(): readonly string[] {
    return this.cursor.nextKeys;
  }
  get typed(): string {
    return this.cursor.typed;
  }
  get remaining(): string {
    return this.cursor.remaining;
  }
  get guide(): string {
    return this.cursor.guide;
  }
  get unitIndex(): number {
    return this.cursor.kanaIndex;
  }
  get unitLength(): number {
    return this.cursor.automaton.kanaLength;
  }
  reset(): void {
    this.cursor.reset();
  }
}

/**
 * 直接入力(英語モードなど)。
 *
 * - 大文字小文字を区別しない
 * - スペース・記号をそのまま1打鍵として扱う(全角記号は半角に正規化)
 * - 表示は元の綴りを保つ(`Apple` は `Apple` のまま出す)
 */
export class DirectValidator implements InputValidator {
  readonly mode = 'direct' as const;

  createMatcher(reading: string): WordMatcher {
    return new DirectWordMatcher(this.toDisplayString(reading));
  }

  toDisplayString(reading: string): string {
    return toHalfWidth(reading);
  }
}

class DirectWordMatcher implements WordMatcher {
  private pos = 0;

  constructor(private readonly text: string) {}

  input(key: string): boolean {
    if (key.length !== 1) return false;
    const expected = this.text[this.pos];
    if (expected === undefined) return false;
    if (expected.toLowerCase() !== key.toLowerCase()) return false;
    this.pos += 1;
    return true;
  }
  get completed(): boolean {
    return this.pos >= this.text.length;
  }
  get nextKeys(): readonly string[] {
    const expected = this.text[this.pos];
    return expected === undefined ? [] : [expected.toLowerCase()];
  }
  get typed(): string {
    return this.text.slice(0, this.pos);
  }
  get remaining(): string {
    return this.text.slice(this.pos);
  }
  get guide(): string {
    return this.text;
  }
  get unitIndex(): number {
    return this.pos;
  }
  get unitLength(): number {
    return this.text.length;
  }
  reset(): void {
    this.pos = 0;
  }
}
